/**
 * selectBridge (DESIGN-client §13): `isEmbedded()` → Discord (client id from
 * `GET /api/discord/config`; a null id means the server is unconfigured → Mock); `?mock=1` →
 * Mock; else Web. The Discord SDK is loaded lazily so the web bundle never ships it.
 */
import type { ActivityBridge } from './ActivityBridge';
import { apiPath, isEmbedded } from './embedded';
import { MockActivityBridge, isMockRequested } from './MockActivityBridge';
import { WebBridge } from './WebBridge';

export interface SelectBridgeOptions {
  embedded?: boolean;
  mock?: boolean;
  fetchFn?: typeof fetch;
  /** Skip the config fetch (tests / known id). */
  clientId?: string | null;
}

export async function fetchDiscordClientId(fetchFn: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetchFn(apiPath('/api/discord/config'));
    if (!res.ok) return null;
    const body = (await res.json()) as { clientId?: string | null };
    return body.clientId ?? null;
  } catch {
    return null;
  }
}

export async function selectBridge(opts: SelectBridgeOptions = {}): Promise<ActivityBridge> {
  const embedded = opts.embedded ?? isEmbedded();
  const mock = opts.mock ?? isMockRequested();
  if (embedded) {
    const clientId = opts.clientId !== undefined ? opts.clientId : await fetchDiscordClientId(opts.fetchFn);
    if (clientId) {
      const { DiscordActivityBridge } = await import('./DiscordActivityBridge');
      return new DiscordActivityBridge(clientId);
    }
    console.warn('[bridge] embedded but no Discord client id configured; using mock bridge');
    return new MockActivityBridge();
  }
  if (mock) return new MockActivityBridge();
  return new WebBridge();
}
